import MainEntryPointApp from "@/MainEntryPointApp.vue";
import vSafeHtml from "@/directive/vSafeHtml";
import { routes } from "@/router/routes";
// Import types for vue-router module augmentation side effect
import "@/router/types";
import { type User, type UserUnion } from "@/services/internalApiV1";
import { isUser } from "@/stores/user";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import FloatingVue from "floating-vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp, nextTick } from "vue";
import VueLazyload from "vue-lazyload";
import { createRouter, createWebHistory } from "vue-router";

import { generateI18n, setI18nTimezone } from "./i18n/i18n.config";
import "./preloadErrorHandler";
import { initSentry, setSentryUser } from "./sentry";
import { type CreateFlashMessage } from "./stores/flash";
import { retryUnlessClientError } from "./utils/queryRetry";
import { routerBeforeEach } from "./utils/routerBeforeEach";
import {
  updatePageTitle,
  type UpdatePageTitleTo,
} from "./utils/updatePageTitle";

const initVue = async (): Promise<void> => {
  const el = document.querySelector('[data-behavior="vue"]') as HTMLElement;
  if (el) {
    const app = createApp(MainEntryPointApp);

    // Register global directive `v-safe-html`, can be used similar to `v-html` but with sanitization
    app.directive("safe-html", vSafeHtml);

    app.config.warnHandler = (msg, instance, trace) => {
      // warnhandler is ignored in production, check anyways
      if (process.env.NODE_ENV !== "development") return;

      if (
        msg.includes("Failed to resolve component") ||
        msg.includes("Unknown custom element")
      ) {
        console.error("Vue Global Warn Handler:", msg, trace);
        throw new Error(msg);
      } else {
        console.warn("Vue Global Warn Handler:", msg, instance, trace);
      }
    };

    const pinia = createPinia();
    pinia.use(piniaPluginPersistedstate);
    app.use(pinia);

    const userStore = useUserStore();
    if (el.dataset.user) {
      const user = JSON.parse(el.dataset.user) as UserUnion;
      userStore.$patch({ user });
    }

    // The seeded user (embedded payload or persisted store) can be stale — e.g.
    // a pupil enrolled in a class after the page was rendered. Refresh from the
    // server before the router is installed so the first `routerBeforeEach` run,
    // including the pupil "redirect away from the start page" check, decides on
    // authoritative data. Runtime freshness (tab focus, bfcache/back) is handled
    // by `useRefetchUsers`.
    if (userStore.isAuthenticated) {
      await userStore.refresh();
    }

    if (el.dataset.flash) {
      const flashStore = useFlashStore();
      const messages = JSON.parse(el.dataset.flash) as string[][];
      messages.forEach((flashMessage) => {
        const [type, message] = flashMessage;
        flashStore.addMessage({ type, message } as CreateFlashMessage);
      });
    }

    if (el.dataset.apps) {
      const appsStore = useAppsStore();
      appsStore.$patch({ apps: JSON.parse(el.dataset.apps) });
    }

    if (el.dataset.isGalleryMode) {
      const isGalleryModeStore = useIsGalleryModeStore();
      isGalleryModeStore.$patch({
        isGalleryMode: JSON.parse(el.dataset.isGalleryMode),
      });
    }

    if (el.dataset.incognito) {
      const incognitoStore = useIncognitoStore();
      incognitoStore.$patch({
        userId: +el.dataset.incognito,
      });
    }

    if (el.dataset.errorStatus) {
      const errorPageStore = useErrorPageStore();
      errorPageStore.$patch({ status: el.dataset.errorStatus });
    }

    const router = createRouter({
      history: createWebHistory(),
      linkExactActiveClass: "router-link-exact-active",
      linkActiveClass: "router-link-active",
      routes,
      scrollBehavior(
        to: { hash: unknown; path: string },
        from: { path?: string },
        _savedPosition: unknown
      ) {
        if (to.hash) {
          const navbarHeight = parseInt(
            document.documentElement.style.getPropertyValue(
              "--navbar-total-height"
            ) || "65",
            10
          );
          return { el: to.hash, top: navbarHeight + 16 };
        }
        // In-page navigations that only change the query on the same route
        // (e.g. list filters) keep the current scroll position instead of
        // jumping to the top — the filtered list can sit well below the fold.
        if (from?.path && to.path === from.path) {
          return false;
        }
        // always scroll to top
        return { top: 0, left: 1 };
      },
    });

    router.beforeEach(routerBeforeEach);

    if (import.meta.env.PROD) {
      // if new app version is deployed, force reload
      // in development we don't want this behavior, because it's really annoying
      router.onError((error: { message: string }, to: { fullPath: string }) => {
        if (
          error.message.includes(
            "Failed to fetch dynamically imported module"
          ) ||
          error.message.includes("error loading dynamically imported module") ||
          error.message.includes("Importing a module script failed")
        ) {
          window.location.href = to.fullPath;
        }
      });
    }

    if (import.meta.env.PROD) {
      initSentry(app, router);

      // Watch user store and update Sentry context on any change (login/logout)
      // Uses pseudonymous toolsUserId for GDPR compliance
      const userStore = useUserStore();
      watch(
        () => userStore.user,
        (user) => {
          const userId =
            user && "toolsUserId" in user ? user.toolsUserId : "LoggedOutUser";
          setSentryUser(userId);
        },
        { immediate: true }
      );
    }

    const queryClient = new QueryClient({
      defaultOptions: {
        queries: { retry: retryUnlessClientError },
      },
    });
    app.use(VueQueryPlugin, {
      queryClient,
      // workaround, see https://github.com/vuejs/devtools-next/issues/317
      enableDevtoolsV6Plugin: true,
    });
    app.provide("queryClient", queryClient);

    const i18n = generateI18n();
    app.use(i18n);

    // Sync user timezone to i18n datetime formats
    // Block scope isolates userStore from the Sentry watcher's userStore (L129)
    {
      const userStore = useUserStore();
      const isValidTz = (tz: string): boolean => {
        try {
          Intl.DateTimeFormat(undefined, { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      };
      watch(
        () => userStore.user,
        (user) => {
          if (isUser(user)) {
            const tz = (user as User).timezone;
            setI18nTimezone(tz && isValidTz(tz) ? tz : "Europe/Berlin");
          } else {
            setI18nTimezone("Europe/Berlin");
          }
        },
        { immediate: true }
      );
    }

    app.use(router);

    app.use(FloatingVue);

    app.use(VueLazyload, {
      observer: true,
    });

    // If the server rendered the SPA for an error (403/404/422), navigate to
    // the error route before mounting so the original URL's page never mounts.
    const errorPageStore = useErrorPageStore();
    if (errorPageStore.status) {
      // Await so navigation to the error route completes before mount —
      // otherwise the original URL's page component mounts (and fires API
      // requests for the inaccessible resource) before the error route resolves.
      await router.replace({
        name: "error",
        params: { status: errorPageStore.status },
      });
    }

    app.mount(el);

    // Update title after each route change (moved here after i18n is initialized)
    router.afterEach((to) => {
      // Wait for next tick to ensure the route is fully loaded
      void nextTick(() => {
        updatePageTitle(to as UpdatePageTitleTo, i18n);
      });
    });
  }
};

void initVue();
